import { Button } from '@design-system';
import { useAnalyticsEmitter } from '@utils/analytics/emitter';
import { TFunction, useTranslation } from 'next-i18next';
import React, { useCallback, useState } from 'react';

import { getProductStatus } from '../../../helpers/purchasing';
import { FlowConfig, useFlow } from '../../../providers/FlowProvider';
import { ProductStatus } from '../../../types/Analytics';
import {
  ProductPurchaseDetails,
  ProductPurchasingController,
} from '../../../types/Purchasing';
import { ButtonLocation } from '../../../types/Subscriptions';
import { NFTProductType } from '../../../types/web3/product';
import { TrackEnquiryClicked } from '../../../utils/analytics';
import { storyblokProductReleaseType } from '../../../utils/storyblokReleaseType';
import { SimpleDialog } from '../../Dialog/SimpleDialog';
import { EnquiryForm } from '../../Enquire/EnquiryForm';
import { EnquirySuccess } from '../../Enquire/EnquirySuccess';
import { FewLeft } from './FewLeft';

type Props<T extends ProductPurchaseDetails> = {
  purchasingController?: ProductPurchasingController<T>;
  product: ProductType | NFTProductType;
  analytics?: Analytics;
  buttonLocation?: ButtonLocation;
};

const buildEnquiryFlow = (
  product: ProductType,
  t: TFunction,
  purchasingDetails?: ProductPurchaseDetails | null,
): FlowConfig => {
  return {
    name: 'enquiry',
    steps: [
      {
        id: 'enquiry',
        title: t('enquiries:drawers.title'),
        hideSpacing: true,
        analytics: {
          name: 'enquiry form',
          properties: {
            'product title': product.title,
          },
        },
        content: (
          <EnquiryForm
            product={product}
            purchasingDetails={purchasingDetails}
          />
        ),
      },
      {
        id: 'success',
        hideBackButton: true,
        hideHeader: true,
        hideSpacing: true,
        analytics: {
          name: 'enquiry success',
          properties: {
            'product title': product.title,
          },
        },
        content: <EnquirySuccess productTitle={product.title} />,
      },
    ],
  };
};

export const EnquireButton: React.FC<Props<ProductPurchaseDetails>> = ({
  analytics,
  buttonLocation = 'product page',
  product,
  purchasingController,
}) => {
  const { t } = useTranslation(['product', 'enquiries']);
  const { startFlow } = useFlow();
  const [showDialog, setShowDialog] = useState(false);

  const productId = !('tokenId' in product) ? product.id[0]?.id : '';

  const [emitter] = useAnalyticsEmitter();

  const handleHowToClick = useCallback(() => {
    emitter('', 'how clicked', {
      type: 'private',
    });
  }, [, emitter]);

  const handleEnquireClick = useCallback(() => {
    const isNFTProduct = 'tokenId' in product;
    const artist = product.productArtist?.[0] as ResolvedRelatedArtist;
    const productStatus: ProductStatus = !isNFTProduct
      ? getProductStatus(product, purchasingController)
      : 'unavailable';

    if (analytics && purchasingController?.product) {
      TrackEnquiryClicked({
        page: analytics.page,
        context: analytics.context,
        artistName: artist.artist.name,
        buttonLocation,
        productId,
        productReleaseType: storyblokProductReleaseType(product),
        productStatus,
        productTitle: product.title,
        productPricesAndCurrencies: !isNFTProduct
          ? product.pricesAndAvailability?.pricesAndCurrencies
          : undefined,
        productPrice: purchasingController?.product.price,
        productCurrencyCode: purchasingController?.product.currency,
        type: 'clicked',
      });
    }

    startFlow(
      buildEnquiryFlow(
        product as ProductType,
        t,
        purchasingController?.product,
      ),
    );
  }, [
    analytics,
    buttonLocation,
    purchasingController,
    product,
    productId,
    startFlow,
    t,
  ]);

  return (
    <div className="flex w-full flex-col items-center gap-y-3">
      <FewLeft
        className="mb-3"
        tags={purchasingController?.product?.tags || []}
      />
      <Button
        fullWidth
        label={t('subscriptionCta.private.buttonEnquireTitle')}
        onClick={handleEnquireClick}
        variant="primary"
      />
      <button
        className="hyperlink"
        onClick={() => {
          setShowDialog(true);
          handleHowToClick();
        }}
        type="button"
      >
        {t('subscriptionCta.private.learnMoreDialog.title')}
      </button>
      <SimpleDialog
        onClose={() => setShowDialog(false)}
        open={showDialog}
        title={t('subscriptionCta.private.learnMoreDialog.title')}
      >
        <div className="flex flex-col gap-y-6">
          <p>{t('subscriptionCta.private.learnMoreDialog.paragraph')}</p>
          <Button
            fullWidth
            label={t('subscriptionCta.private.buttonEnquireTitle')}
            onClick={() => {
              setShowDialog(false);
              handleEnquireClick();
            }}
            variant="primary"
          />
        </div>
      </SimpleDialog>
    </div>
  );
};
